import React from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCalendarCheck, FaSyncAlt, FaHandshake } from 'react-icons/fa';
import theme from '../../theme';
import b2bImage from '../../assets/images/b2b-set.png';
const HeroSection = styled.section`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: ${theme.colors.white};
  padding: 6rem 0 5rem;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
    opacity: 0.5;
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    text-align: center;
    gap: 2rem;
  }
`;

const HeroText = styled.div`
  flex: 1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-bottom: 2rem;
  }
`;

const HeroTag = styled.div`
  display: inline-block;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 30px;
  padding: 0.5rem 1.5rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
  font-size: ${theme.fontSizes.small};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.small};
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  
  span {
    color: ${theme.colors.white};
    background-color: rgba(0, 0, 0, 0.1);
    padding: 0 0.5rem;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${theme.fontSizes.large};
  margin-bottom: 2rem;
  opacity: 0.9;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.medium};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-block;
  background-color: ${theme.colors.white};
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.white};
  padding: 0.75rem 2rem;
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  text-align: center;
  
  &:hover {
    background-color: transparent;
    color: ${theme.colors.white};
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: ${theme.colors.white};
  border: 2px solid ${theme.colors.white};
  padding: 0.75rem 2rem;
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
  border-radius: ${theme.borderRadius};
  transition: ${theme.transition};
  text-align: center;
  
  &:hover {
    background-color: ${theme.colors.white};
    color: ${theme.colors.primary};
  }
`;

const HeroImage = styled.div`
  flex: 1.5;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-height: 700px;
  margin-top: -12rem;
  
  img {
    width: 100%;
    max-width: 1024px;
    height: auto;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    align-items: center;
    min-height: 600px;
    margin-top: -2rem;
    
    img {
      max-width: 100%;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-height: 500px;
    margin-top: -1rem;
  }
`;

const OrderPlacedBadge = styled.div`
  position: absolute;
  top: 250px;
  right: -200px;
  transform: translateX(-50%);
  background: white;
  color: #0EA5E9;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: ${theme.fontSizes.small};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.15),
    0 5px 15px rgba(0, 0, 0, 0.1);
  z-index: 4;
  animation: slideUp 0.6s ease-out;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
  
  .check-icon {
    width: 24px;
    height: 24px;
    background: #0EA5E9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    bottom: 20px;
    padding: 0.875rem 1.25rem;
    font-size: ${theme.fontSizes.xsmall};
    
    .check-icon {
      width: 20px;
      height: 20px;
      
      svg {
        width: 12px;
        height: 12px;
      }
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;
    margin: 1rem auto 0;
    width: fit-content;
    
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

const SocialProofList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: -1.5rem;
  align-self: center;
`;

const SocialProofItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 0.75rem 1.5rem;
  border-radius: 10px;

  .check {
    width: 24px;
    height: 24px;
    min-width: 24px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .proof-text {
    font-size: 1.05rem;
    font-weight: 600;
    color: white;
    line-height: 1.4;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.65rem 1rem;

    .proof-text {
      font-size: 0.95rem;
    }
  }
`;

const HeroTrustRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: ${theme.breakpoints.tablet}) {
    justify-content: center;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 0.75rem;
  }
`;

const HeroTrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);

  svg {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    flex-shrink: 0;
  }
`;

const Hero = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  
  return (
    <HeroSection>
      <div className="container">
        <HeroContent>
          <HeroText>
            <HeroTag>{t('hero.tag')}</HeroTag>
            <HeroTitle>
              {t('hero.title').split(t('hero.titleHighlight'))[0]}
              <span>{t('hero.titleHighlight')}</span>
              {t('hero.title').split(t('hero.titleHighlight'))[1]}
            </HeroTitle>
            <HeroSubtitle dangerouslySetInnerHTML={{ __html: t('hero.subtitle') }} />
            <ButtonGroup>
              <PrimaryButton to={`/${lang}/demo`}>{t('hero.ctaPrimary')}</PrimaryButton>
              <SecondaryButton to={`/${lang}/industries`}>{t('hero.ctaSecondary')}</SecondaryButton>
            </ButtonGroup>
            <HeroTrustRow>
              <HeroTrustItem><FaCalendarCheck /> {t('trustBadges.onboarding')}</HeroTrustItem>
              <HeroTrustItem><FaSyncAlt /> {t('trustBadges.cancel')}</HeroTrustItem>
              <HeroTrustItem><FaHandshake /> {t('trustBadges.noContract')}</HeroTrustItem>
            </HeroTrustRow>
          </HeroText>
          <HeroImage>
            <OrderPlacedBadge>
              <div className="check-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path 
                    d="M5 13l4 4L19 7" 
                    stroke="white" 
                    strokeWidth="2.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span>{t('hero.orderPlaced')}</span>
            </OrderPlacedBadge>
            
            <img src={b2bImage} alt="B2B Platform" style={{marginTop: '50px'}}/>
            <SocialProofList>
              <SocialProofItem>
                <span className="check">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <span className="proof-text">{t('hero.socialProof')}</span>
              </SocialProofItem>
              <SocialProofItem>
                <span className="check">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <span className="proof-text">{t('hero.socialProof2')}</span>
              </SocialProofItem>
            </SocialProofList>
          </HeroImage>
        </HeroContent>
      </div>
    </HeroSection>
  );
};

export default Hero; 